import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Logo } from "@/components/Layout";

export default function AuthPage({ mode }) {
  const { user, signIn } = useAuth();
  const nav = useNavigate();
  const from = useLocation().state?.from;
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const isLogin = mode === "login";
  if (user) return <Navigate to={from || "/"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post(`/auth/${isLogin ? "login" : "register"}`, form);
      signIn(data);
      nav(from || (isLogin ? "/" : "/teams"));
    } catch (ex) { setErr(errMsg(ex)); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#070D18] text-white">
      <div className="hidden lg:flex turf-bg relative items-end p-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-[#070D18]/90 to-transparent" />
        <div className="relative max-w-md tpc-rise">
          <p className="font-display text-6xl font-extrabold uppercase leading-[0.9]">Draw it.<br />Animate it.<br /><span className="text-[#FF8A1F]">Teach it.</span></p>
          <p className="mt-4 text-slate-200">An animated playbook that shows every kid where to line up, when to go, and where to move.</p>
        </div>
      </div>
      <div className="flex flex-col justify-center px-5 py-10 sm:px-12">
        <div className="max-w-sm w-full mx-auto tpc-rise">
          <Logo />
          <h1 className="h1 mt-8">{isLogin ? "Coach sign in" : "Create coach account"}</h1>
          <p className="text-slate-400 mt-2 text-sm">Only coaches need accounts. Players view plays with a private team link or code.</p>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="auth-form">
            {!isLogin && (
              <div><label className="label" htmlFor="name">Your name (optional)</label><input id="name" className="input" value={form.name} onChange={set("name")} data-testid="auth-name-input" /></div>
            )}
            <div><label className="label" htmlFor="email">Email</label><input id="email" type="email" required autoComplete="email" className="input" value={form.email} onChange={set("email")} data-testid="auth-email-input" /></div>
            <div><label className="label" htmlFor="pw">Password {!isLogin && "(8+ characters)"}</label><input id="pw" type="password" required minLength={isLogin ? 1 : 8} autoComplete={isLogin ? "current-password" : "new-password"} className="input" value={form.password} onChange={set("password")} data-testid="auth-password-input" /></div>
            {err && <p className="text-red-300 text-sm" role="alert" data-testid="auth-error">{err}</p>}
            <button className="btn btn-primary w-full text-base" disabled={busy} data-testid="auth-submit-button">{busy ? "Please wait…" : isLogin ? "Sign in" : "Create account"}</button>
          </form>
          <p className="mt-6 text-sm text-slate-400">
            {isLogin ? "New coach? " : "Already have an account? "}
            <Link className="text-[#FF8A1F] font-bold" to={isLogin ? "/register" : "/login"} state={{ from }} data-testid="auth-switch-link">{isLogin ? "Create an account" : "Sign in"}</Link>
          </p>
          <p className="mt-3 text-sm text-slate-400">Player or parent? <Link to="/join" className="text-[#FF8A1F] font-bold" data-testid="auth-join-link">Enter a team code</Link></p>
        </div>
      </div>
    </div>
  );
}
